import * as React from 'react';

import {CollapseExpandAnimation} from 'shells/animation/collapse-expand-animation';
import {Card} from 'shells/card/card';
import {Stack} from 'shells/layout/stack/stack';

import type {
    OnboardingModuleFragment,
    UserEmailCalendarStatusesFragment,
    OnboardingQuotesFragment,
    UserVideoIntegrationsFragment,
} from 'nutshell-graphql-types';

import {writeLocalStorage, readLocalStorage} from '../../../../util/localStorage';

import type {ExplorePageVariant} from '../types';

import {LearnMoreInfo} from './learn-more-info';
import {OnboardingModuleHeader} from './onboarding-module-header';
import {OnboardingStep} from './onboarding-step';

import styles from './onboarding-module.module.css';

const LOCAL_STORAGE_KEY_PREFIX = 'onboarding_module_expanded_';

type Props = {
    module: OnboardingModuleFragment;
    hasFullSchedulerAccess: boolean;
    isAdmin: boolean;
    userEmailCalendarStatuses: UserEmailCalendarStatusesFragment | null | undefined;
    onboardingQuotes: OnboardingQuotesFragment | null | undefined;
    userVideoIntegrations: UserVideoIntegrationsFragment | null | undefined;
    hasClickToCallConnected?: boolean;
    hasPhoneIntegrationConnected?: boolean;
    variant: ExplorePageVariant;
    onEmailCalendarAuthSuccess?: (provider: 'google' | 'microsoft') => void;
};

export function OnboardingModule(props: Props) {
    const localStorageKey = `${LOCAL_STORAGE_KEY_PREFIX}${props.module.type}`;
    const [isExpanded, setIsExpanded] = React.useState<boolean>(() => {
        const storedValue = readLocalStorage(localStorageKey);

        return storedValue != null ? storedValue === 'true' : props.module.type === 'ESSENTIALS';
    });

    const handleSetCollapseState = React.useCallback(
        (newIsExpanded: boolean) => {
            setIsExpanded(newIsExpanded);
            writeLocalStorage(localStorageKey, String(newIsExpanded));
        },
        [localStorageKey]
    );

    return (
        <Card hasBorder={true} borderRadius={8} fitHeight={true}>
            <OnboardingModuleHeader
                module={props.module}
                isExpanded={isExpanded}
                setIsExpanded={handleSetCollapseState}
            />
            {/* @ts-expect-error */}
            <CollapseExpandAnimation isExpanded={isExpanded}>
                <div className='pad-24'>
                    <Stack spacing={24} isHorizontal={true}>
                        <div className={styles['module-steps-container']}>
                            {props.module.steps.map((step) => (
                                <OnboardingStep
                                    key={step.type}
                                    step={step}
                                    hasFullSchedulerAccess={props.hasFullSchedulerAccess}
                                    isAdmin={props.isAdmin}
                                    userEmailCalendarStatuses={props.userEmailCalendarStatuses}
                                    onboardingQuotes={props.onboardingQuotes}
                                    userVideoIntegrations={props.userVideoIntegrations}
                                    hasClickToCallConnected={props.hasClickToCallConnected}
                                    hasPhoneIntegrationConnected={
                                        props.hasPhoneIntegrationConnected
                                    }
                                    variant={props.variant}
                                    onEmailCalendarAuthSuccess={props.onEmailCalendarAuthSuccess}
                                />
                            ))}
                        </div>
                        <div className={styles['module-learn-more-container']}>
                            <LearnMoreInfo moduleType={props.module.type} />
                        </div>
                    </Stack>
                </div>
            </CollapseExpandAnimation>
        </Card>
    );
}
